import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { MessageService } from '@progress/kendo-angular-l10n';

import { languageFor } from '../common/formatting-locale';
import { KENDO_MESSAGES_DE } from './kendo-messages.de';

/**
 * German texts for the Kendo components this app renders (#178 slice E, decision 4).
 *
 * **Kendo's built-in messages are English**, and they were showing through everywhere the app's
 * own `i18n` strings did not reach: the grid's „No records available.", „Sort Ascending" and every
 * filter operator, the dropdowns' „NO DATA FOUND", the date picker's „Today", the file picker's
 * „Select files...", the whole editor toolbar. A German page with English furniture. #648 had
 * covered eight pager keys; this covers what is actually on screen.
 *
 * **Only German lives here, and only under the German bundle.** Kendo's defaults *are* the English
 * translation — correct, vendor-maintained and already there — so the English bundle answers
 * `undefined` for every key and gets them. That is also why these strings are plain literals
 * rather than `$localize` calls: wrapping them would extract 260-odd units whose English target
 * would be a hand-copy of the default Kendo already ships, which is exactly the dead weight the
 * 467 extracted Kendo units already are.
 *
 * Those 467 units, incidentally, cannot be removed from the xlf as the issue hoped: they come from
 * `ng extract-i18n` compiling Kendo's own templates out of `node_modules`, and there is no exclude
 * for that. They are also not a route to German — their `<source>` is Kendo's English text, and a
 * source is not something a translation file can change. Hence a MessageService.
 *
 * Returning `undefined` for an unknown key is the contract: Kendo then uses its own default, so a
 * key this file has not covered degrades to English rather than to a blank label.
 */
@Injectable()
export class KendoMessagesService extends MessageService {
  private readonly messages: Readonly<Record<string, string>>;

  constructor() {
    const localeId = inject(LOCALE_ID);

    super();
    this.messages = languageFor(localeId) === 'de' ? KENDO_MESSAGES_DE : {};
  }

  public override get(key: string): string | undefined {
    return this.messages[key];
  }
}
